import { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, TextField, MenuItem, Button, Chip, LinearProgress,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import TuneIcon from '@mui/icons-material/Tune';
import RuleIcon from '@mui/icons-material/Rule';
import { getConfigRules, getConfigStatus, listSecurityHubAccounts } from '../../../services/securityhubApi';
import { SETUP_STATUSES } from './configStatus';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalEmptyState, PortalErrorState, PortalSectionHeader,
} from '../../../components/portal/PortalStates';
import { formatStatusLabel } from '../../../utils/statusLabel';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  sectionCardSx, listCardSx, tableHeadRowSx, headCellSx, rowSx, bodyCellSx,
  statusChipSx, emptyTextSx, actionRowSx,
} from '../portalStyles';

export default function ConfigRules() {
  const [setupCells, setSetupCells] = useState([]); // [{accountId, region, status}]
  const [nameMap, setNameMap] = useState({});       // accountId -> friendly name
  const [account, setAccount] = useState('');
  const [region, setRegion] = useState('');
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCells, setLoadingCells] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [cs, a] = await Promise.all([getConfigStatus(), listSecurityHubAccounts()]);
        const cells = (cs?.data?.recorders || []).filter((r) => SETUP_STATUSES.includes(r.status));
        setSetupCells(cells);
        const accts = a?.data?.accounts || a?.data || [];
        const m = {};
        accts.forEach((x) => { if (x.accountId) m[x.accountId] = x.accountName || x.name || x.accountId; });
        setNameMap(m);
      } catch (e) { setError(e.message); } finally { setLoadingCells(false); }
    })();
  }, []);

  // Only accounts/regions where AWS Config is set up are selectable.
  const accountOptions = [...new Set(setupCells.map((r) => r.accountId))];
  const regionOptions = account
    ? [...new Set(setupCells.filter((r) => r.accountId === account).map((r) => r.region))]
    : [];

  const load = async () => {
    if (!account || !region) return;
    setLoading(true);
    try {
      const res = await getConfigRules(account, region);
      setRules(res?.data || []);
      setError(null);
    } catch (e) { setError(e.message); setRules(null); } finally { setLoading(false); }
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
      <PortalPageHeader icon={<StorageIcon sx={{ fontSize: 24 }} />} title="Config rules"
        subtitle="AWS Config rules captured during the last AWS Config refresh" />
      </motion.div>

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
        <PortalSectionHeader
          icon={<TuneIcon sx={{ fontSize: 20 }} />}
          title="Account and region"
          subtitle="Rules are captured per account and region — pick a pair, then load them"
          mb={2}
        />
        <Box sx={actionRowSx}>
          <TextField select size="small" label="Account" value={account}
            onChange={(e) => { setAccount(e.target.value); setRegion(''); setRules(null); }}
            sx={{ minWidth: 0, flex: '1 1 240px', maxWidth: 360 }}>
            {accountOptions.map((a) => (
              <MenuItem key={a} value={a}>{nameMap[a] ? `${nameMap[a]} (${a})` : a}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Region" value={region} disabled={!account}
            onChange={(e) => { setRegion(e.target.value); setRules(null); }}
            sx={{ minWidth: 0, flex: '1 1 160px', maxWidth: 240 }}>
            {regionOptions.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>
          <Button variant="contained" disabled={!account || !region || loading} onClick={load}>Load rules</Button>
        </Box>
      </Paper>
      </motion.div>

      {error && (
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} />
        </motion.div>
      )}

      {!loadingCells && accountOptions.length === 0 && (
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
            <PortalEmptyState
              icon={<StorageIcon sx={{ fontSize: 28 }} />}
              title="No accounts with AWS Config"
              description="Enable AWS Config recording on the AWS Config page first — its rules appear here after the next refresh."
            />
          </Paper>
        </motion.div>
      )}

      {loading && !rules && (
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading Config rules…" />
          </Paper>
        </motion.div>
      )}

      {/* A re-load of the same account/region keeps the previous result on
          screen behind a progress bar rather than collapsing the card. */}
      {rules && (
        <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={listCardSx}>
          <Box sx={{ px: 3, pt: 3, pb: 2 }}>
            <PortalSectionHeader
              icon={<RuleIcon sx={{ fontSize: 20 }} />}
              title={`Config rules (${rules.length})`}
              subtitle={`Captured for ${nameMap[account] ? `${nameMap[account]} (${account})` : account} · ${region}`}
              mb={0}
            />
          </Box>
          {loading && <LinearProgress />}
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={headCellSx}>Rule name</TableCell>
                  <TableCell sx={headCellSx}>Source</TableCell>
                  <TableCell sx={headCellSx}>State</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((r, index) => (
                  <TableRow key={r.name} hover sx={rowSx(index)}>
                    <TableCell sx={bodyCellSx}>{r.name}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>{r.sourceIdentifier}</TableCell>
                    <TableCell sx={bodyCellSx}><Chip size="small" label={formatStatusLabel(r.state)} sx={statusChipSx(r.state === 'ACTIVE' ? 'success' : 'neutral')} /></TableCell>
                  </TableRow>
                ))}
                {rules.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <Typography sx={emptyTextSx}>
                        No Config rules captured yet — click Refresh on the AWS Config page to populate them.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        </motion.div>
      )}
    </motion.div>
  );
}
